import { useState, useCallback } from 'react';
import { useAppSelector } from '../../../../app/hooks';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getPlatformUserId } from '../../../../utils/utils';
import { updateFolderMapping, updateFolderMappingBulk } from '../../../../store/dataRoom/apiHelper';
import { FolderInfo } from '../../useCallApiToFetchFileData';

type SelectedOption = {
    selected: string;
    contextId?: string;
    parentFolder?: string;
};

type UseDataRoomModalsLogicProps = {
    setIsFetchingData: React.Dispatch<React.SetStateAction<boolean>>;
    docId?: number;
    currentDocType?: string;
    fetchFiles?: Function;
    dataTypesSubFolderList?: Record<string, FolderInfo>;
    selectedDocIds?: number[];
    onMoveSuccess?: () => void;
};

const useDataRoomModalsLogic = ({
    setIsFetchingData,
    docId,
    fetchFiles,
    currentDocType,
    dataTypesSubFolderList,
    selectedDocIds,
    onMoveSuccess,
}: UseDataRoomModalsLogicProps) => {
    const params = useParams();
    const investorOrgId = useAppSelector((state) => state.user.userData.investorOrgId);
    const [selectedOption, setSelectedOption] = useState<SelectedOption>({
        selected: '',
    });

    const handleMetaDataUpdate = useCallback(async () => {
        if (!selectedOption || !docId) return;

        // Find the selected folder info to get displayName
        const selectedFolderInfo = selectedOption.contextId ? dataTypesSubFolderList?.[selectedOption.contextId] : undefined;

        // Check if multiple files are selected (bulk move)
        const isBulkMove = selectedDocIds && selectedDocIds.length > 1;

        let payload;
        let updateFunction;

        if (isBulkMove) {
            // Use bulk endpoint for multiple files
            payload = {
                orgId: params.id ?? '',
                bulkFolderUpdate: {
                    directory: selectedOption.contextId,
                    mnemosyneDocIds: selectedDocIds.map(String),
                    updatedAt: new Date(),
                    userId: getPlatformUserId(),
                    displayName: selectedFolderInfo?.displayName || selectedOption.selected,
                },
            };
            updateFunction = updateFolderMappingBulk;
        } else {
            // Use single endpoint for single file
            payload = {
                orgId: params.id ?? '',
                docData: {
                    directory: selectedOption.contextId,
                    mnemosyneDocId: docId,
                    updatedAt: new Date(),
                    userId: getPlatformUserId(),
                    displayName: selectedFolderInfo?.displayName || selectedOption.selected,
                },
            };
            updateFunction = updateFolderMapping;
        }

        updateFunction(payload)
            .then((response) => {
                // Use backend response message if available, otherwise use default
                let successMessage;
                if (isBulkMove && response?.message) {
                    successMessage = response.message;
                } else if (isBulkMove) {
                    successMessage = `${selectedDocIds.length} files moved successfully to selected folder`;
                } else {
                    successMessage = 'File moved successfully to selected folder';
                }
                toast.success(successMessage);
                setIsFetchingData(true);
                fetchFiles?.();
                // Call the success callback to clear selections
                if (onMoveSuccess) {
                    onMoveSuccess();
                }
            })
            .catch((err) => {
                console.error('Error while moving the file:', err);
            });
    }, [selectedOption, docId, investorOrgId, params.id, dataTypesSubFolderList, selectedDocIds, onMoveSuccess]);

    return { selectedOption, setSelectedOption, handleMetaDataUpdate };
};

export default useDataRoomModalsLogic;
